import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Activities.css';

function FadeSection({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add('visible'); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={`fade-in ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

const activities = [
  {
    id: 'tree-planting',
    icon: '🌳',
    title: 'Tree Planting Drives',
    subtitle: 'In Coordination with Forest Department',
    color: '#1a5c2e',
    bg: 'linear-gradient(135deg, #f0faf4, #e8f7ed)',
    desc: 'Our flagship activity — organized tree planting drives across India, conducted in full partnership with the Forest Department. Every sapling is selected for its ecological impact and climate resilience.',
    points: [
      'Monthly plantation drives in 50+ cities',
      'Forest Department certified species selection',
      'Community participation and awareness',
      'GPS-tracked plantation zones',
      'Annual biodiversity reports',
      'Valentine\'s Day "Plant a Love" campaign',
    ],
    impact: '10,000+ Trees | 50+ Cities | 12+ Forest Partners',
  },
  {
    id: 'awareness',
    icon: '📢',
    title: 'Climate Awareness Drives',
    subtitle: 'Education & Action',
    color: '#4a9eda',
    bg: 'linear-gradient(135deg, #f0f7ff, #e8f0fb)',
    desc: 'Knowledge is the first step to change. Our awareness drives educate communities about climate change, carbon footprints, and what every individual can do to protect the environment.',
    points: [
      'School & college environmental workshops',
      'Community climate education programs',
      'Social media awareness campaigns',
      'Street rallies and public demonstrations',
      'Documentary screenings and panel discussions',
      'Youth ambassador programs',
    ],
    impact: '200+ Workshops | 50,000+ People Reached',
  },
  {
    id: 'clean-drive',
    icon: '🧹',
    title: 'Clean India Drive',
    subtitle: 'Community Cleanliness Campaign',
    color: '#ff6b2b',
    bg: 'linear-gradient(135deg, #fff5f0, #ffede5)',
    desc: 'A cleaner India is a greener India. Our clean drive volunteers hit the streets, parks, beaches, and public spaces — transforming communities one cleanup at a time.',
    points: [
      'Weekend neighborhood cleanup events',
      'Beach and river clean drives',
      'Public park restoration',
      'Waste segregation training',
      'Coordination with local municipalities',
      'Plastic-free pledge campaigns',
    ],
    impact: '500+ Events | 100+ Locations | 5,000+ Volunteers',
  },
  {
    id: 'calltree',
    icon: '🌱',
    title: 'CallTree Project',
    subtitle: 'Digital Tree Gifting Platform',
    color: '#c9a227',
    bg: 'linear-gradient(135deg, #fffbf0, #fff5d6)',
    desc: 'calltree.in brings environmental action into the digital age. Gift trees for special occasions — birthdays, anniversaries, Valentine\'s Day — building living memories that last generations.',
    points: [
      'Online tree registration and dedication',
      'Valentine\'s Day "Plant a Love" campaign',
      'Birthday and anniversary tree gifting',
      'Memorial tree plantations',
      'Digital tree tracking and certificates',
      'Share your tree memory on social media',
    ],
    impact: 'Launch Campaign | Growing Nationally | Going Global',
  },
];

const upcoming = [
  { date: 'Feb 14', title: 'Plant a Love — Valentine\'s Campaign', loc: 'Pan India', icon: '❤️' },
  { date: 'Mar 21', title: 'World Forest Day Drive', loc: 'Multiple Cities', icon: '🌲' },
  { date: 'Apr 22', title: 'Earth Day Mega Plantation', loc: 'Forest Dept. Sites', icon: '🌍' },
  { date: 'Jun 5', title: 'World Environment Day Rally', loc: 'All India', icon: '📢' },
  { date: 'Oct 2', title: 'Gandhi Jayanti Clean Drive', loc: '500+ Locations', icon: '🧹' },
];

export default function Activities() {
  return (
    <div className="activities-page">
      {/* HERO */}
      <section className="page-hero activities-hero">
        <div className="activities-hero__bg" />
        <div className="page-hero__overlay" />
        <div className="container page-hero__content">
          <span className="tag" style={{ background: 'rgba(255,255,255,0.15)', color: 'var(--gold-light)' }}>
            Our Activities
          </span>
          <h1 className="page-hero__title">
            Every Action<br />Counts for the Planet
          </h1>
          <p className="page-hero__sub">
            Tree planting drives, climate awareness campaigns, community clean drives —
            Y2K Global Nexus is on the ground, making real environmental impact across India.
          </p>
        </div>
      </section>

      {/* ACTIVITIES */}
      <section className="act-list section-padding">
        <div className="container">
          {activities.map(({ id, icon, title, subtitle, color, bg, desc, points, impact }, i) => (
            <FadeSection key={id}>
              <div className={`act-item ${i % 2 === 1 ? 'act-item--reverse' : ''}`}>
                <div className="act-item__visual" style={{ background: bg }}>
                  <div className="act-item__icon" style={{ color }}>{icon}</div>
                  <div className="act-item__impact">{impact}</div>
                </div>
                <div className="act-item__content">
                  <div className="act-item__subtitle" style={{ color }}>{subtitle}</div>
                  <h2 className="act-item__title">{title}</h2>
                  <p className="act-item__desc">{desc}</p>
                  <ul className="act-item__points">
                    {points.map((p, pi) => (
                      <li key={pi}><span style={{ color }}>✓</span> {p}</li>
                    ))}
                  </ul>
                  <Link to="/contact" className="btn-primary" style={{ marginTop: '24px', display: 'inline-flex' }}>
                    Participate →
                  </Link>
                </div>
              </div>
            </FadeSection>
          ))}
        </div>
      </section>

      {/* UPCOMING EVENTS */}
      <section className="act-events section-padding" style={{ background: 'var(--cream)' }}>
        <div className="container">
          <FadeSection>
            <div style={{ textAlign: 'center', marginBottom: '56px' }}>
              <span className="tag">Calendar</span>
              <h2 className="section-title">Upcoming Events</h2>
              <p className="section-subtitle" style={{ margin: '0 auto' }}>
                Mark your calendar and join us for these upcoming environmental initiatives.
              </p>
            </div>
          </FadeSection>
          <div className="act-events__list">
            {upcoming.map(({ date, title, loc, icon }, i) => (
              <FadeSection key={i} delay={i * 80}>
                <div className="act-event-card">
                  <div className="act-event-card__date">
                    <span>{date.split(' ')[0]}</span>
                    <strong>{date.split(' ')[1]}</strong>
                  </div>
                  <div className="act-event-card__icon">{icon}</div>
                  <div className="act-event-card__info">
                    <h3>{title}</h3>
                    <p>📍 {loc}</p>
                  </div>
                  <Link to="/contact" className="act-event-card__btn">
                    Register
                  </Link>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* VOLUNTEER */}
      <section className="act-volunteer">
        <div className="container">
          <FadeSection>
            <div className="act-volunteer__inner">
              <div>
                <h2>Become a Y2K <span className="gold-text">Field Champion</span></h2>
                <p>
                  Join our army of green warriors. Volunteer for tree planting, clean drives,
                  awareness campaigns, and help us build a better India — and a better world.
                </p>
              </div>
              <Link to="/contact" className="btn-gold">🤝 Volunteer Now</Link>
            </div>
          </FadeSection>
        </div>
      </section>
    </div>
  );
}
